const pageElement = (component, index) => {
    if (component._previewOwner && !component._previewOwner.isConnected) return null;
    return (component._previewOwner || document).querySelector('#page_content_' + index);
};
const markerAttribute = 'data-diagramacao-caret';
const significant = node => !!node && (!!node.textContent.replace(/[\s\u200b\u00a0]/g, '') || ((node.nodeType === 1 || node.nodeType === 11) && (node.matches?.('img,svg,canvas,table,hr,input') || node.querySelector('img,svg,canvas,table,hr,input'))));
const overflows = element => element.scrollHeight > element.clientHeight + 1 || element.scrollWidth > element.clientWidth + 1;

function preparePage(element) {
    // Jodit adds min-height:100%, which includes the header/footer space of the whole sheet.
    element.style.minHeight = '0px';
    Array.from(element.children).forEach(block => {
        if (block.hasAttribute(markerAttribute)) return;
        block.style.maxWidth = '100%';
        block.querySelectorAll('div[style],p[style],hr[style],section[style],ul[style],ol[style],li[style],blockquote[style]').forEach(child => {
            if (/px$/.test(child.style.maxWidth)) child.style.maxWidth = '100%';
        });
    });
}

function splitBlockChildren(block) {
    if (block.nodeType !== 1 || block.matches('table,img,svg,canvas')) return null;
    let last;
    if (block.childNodes.length > 1 && Array.from(block.children).some(child => child.matches('p,div,section,ul,ol,blockquote,br'))) {
        last = block.lastChild;
    } else if (block.childNodes.length === 1) {
        last = splitBlockChildren(block.firstChild);
    }
    if (!last) return null;
    const tail = block.cloneNode(false);
    tail.removeAttribute('id');
    tail.append(last);
    return tail;
}

// A paragraph may be longer than an entire sheet. Split its text while preserving inline formatting.
function splitTextBlock(page, block) {
    if (block.nodeType !== 1 && block.nodeType !== 3) return null;
    if (block.nodeType !== 3 && block.matches('table,img,svg,canvas')) return null;
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    const textNodes = block.nodeType === 3 ? [block] : [];
    let node;
    while ((node = walker.nextNode())) textNodes.push(node);
    const text = textNodes.map(item => item.data).join('');
    if (text.length < 2) return null;
    const position = offset => {
        for (const textNode of textNodes) {
            if (offset <= textNode.length) return [textNode, offset];
            offset -= textNode.length;
        }
        return [textNodes[textNodes.length - 1], textNodes[textNodes.length - 1].length];
    };
    let low = 1, high = text.length - 1, fit = 0;
    while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        let probe;
        if (block.nodeType === 3) probe = document.createTextNode(text.slice(0, middle));
        else {
            const range = document.createRange();
            range.selectNodeContents(block);
            range.setEnd(...position(middle));
            probe = block.cloneNode(false);
            probe.append(range.cloneContents());
        }
        block.replaceWith(probe);
        const overflow = overflows(page);
        probe.replaceWith(block);
        if (overflow) high = middle - 1;
        else { fit = middle; low = middle + 1; }
    }
    if (!fit) return null;
    // Prefer a word boundary. Never split a surrogate pair in the middle.
    const space = text.slice(0, fit).search(/\s+\S*$/);
    if (space > 0) fit = space + 1;
    if (/[\uD800-\uDBFF]/.test(text[fit - 1]) && /[\uDC00-\uDFFF]/.test(text[fit])) fit--;
    if (!fit) return null;
    if (block.nodeType === 3) return block.splitText(fit);
    const range = document.createRange();
    range.setStart(...position(fit));
    range.setEnd(block, block.childNodes.length);
    const tail = block.cloneNode(false);
    tail.removeAttribute('id');
    tail.append(range.extractContents());
    return tail;
}

function bookmark() {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !selection.isCollapsed) return null;
    const range = selection.getRangeAt(0);
    const page = (range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement)?.closest('[id^="page_content_"]');
    if (!page) return null;
    const marker = document.createElement('span');
    marker.setAttribute(markerAttribute, '');
    marker.style.cssText = 'display:inline-block;width:0;height:0;overflow:hidden;line-height:0';
    range.insertNode(marker);
    return marker;
}

function restoreBookmark(marker) {
    if (!marker) return;
    if (marker.isConnected) {
        const range = document.createRange();
        range.setStartBefore(marker);
        range.collapse(true);
        const page = marker.closest('[id^="page_content_"]');
        page?.focus({ preventScroll: true });
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        marker.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    marker.remove();
}

function snapshot(component, index) {
    const element = pageElement(component, index);
    if (!element || !component.pages_new[index]) return;
    const copy = element.cloneNode(true);
    copy.querySelectorAll('[' + markerAttribute + '], [data-jodit-selection_marker], [id^="jodit_selection_marker"]').forEach(marker => marker.remove());
    const children = Array.from(element.children).filter(child => !child.hasAttribute(markerAttribute));
    const previous = component.pages_new[index];
    component.pages_new[index] = { ...previous, value: copy.innerHTML, value_content: copy.textContent,
        elements: children, ids: children.map(child => child.id), new_ids: children.map(child => child.id),
        heights: children.map(child => child.offsetHeight), height: children.reduce((height, child) => height + child.offsetHeight, 0) };
}

function trimTrailingBlankLines(element) {
    while (element.lastChild) {
        const last = element.lastChild;
        if (!significant(last) && !(last.nodeType === 1 && (last.hasAttribute(markerAttribute) || last.querySelector('[' + markerAttribute + ']')))) {
            last.remove();
            continue;
        }
        if (last.nodeType === 1 && !last.matches('img,svg,canvas,table,hr,input')) trimTrailingBlankLines(last);
        break;
    }
}

const emptyPage = () => ({ value: '', value_content: '', ids: [], new_ids: [], elements: [], heights: [], height: 0 });
const updateState = (component, values) => new Promise(resolve => component.setState(values, resolve));

async function paginate(component, start, joinPrevious, joinIndex = start) {
    if (component._previewOwner && !component._previewOwner.isConnected) return;
    const marker = bookmark();
    try {
        if (joinPrevious && joinIndex > 0) {
            const previous = pageElement(component, joinIndex - 1);
            const current = pageElement(component, joinIndex);
            if (previous && current) {
                trimTrailingBlankLines(previous);
                const last = previous.lastElementChild;
                const first = current.firstElementChild;
                // Merge ordinary text paragraphs as a native Backspace would; retain question boundaries.
                if (last?.matches('p:not(.questao):not(.alternativa):not(.categoria):not(.dividor)') && first?.matches('p:not(.questao):not(.alternativa):not(.categoria):not(.dividor)')) {
                    last.append(...Array.from(first.childNodes));
                    first.remove();
                }
                start = Math.min(start, joinIndex - 1);
            }
        }
        for (let index = start; index < component.pages_new.length; index++) {
            const current = pageElement(component, index);
            if (!current) continue;
            preparePage(current);
            while (overflows(current)) {
                const blocks = Array.from(current.children).filter(child => !child.hasAttribute(markerAttribute));
                let last = current.lastChild;
                if (!last) break;
                if (current.childNodes.length === 1 || (blocks.length === 1 && last === blocks[0])) {
                    const split = splitBlockChildren(last) || splitTextBlock(current, last);
                    if (!split) break;
                    last = split;
                }
                if (!pageElement(component, index + 1)) {
                    component.pages_new.push(emptyPage());
                    await updateState(component, { pages_new: [...component.pages_new] });
                }
                const next = pageElement(component, index + 1);
                if (!next) break;
                next.prepend(last);
                preparePage(next);
            }
            // Pull content back when space was released; undo the tentative move if it does not fit.
            const next = pageElement(component, index + 1);
            while (next?.firstChild && !overflows(current)) {
                const first = next.firstChild;
                current.append(first);
                preparePage(current);
                if (overflows(current)) {
                    const tail = splitTextBlock(current, first);
                    if (tail) next.prepend(tail);
                    else next.prepend(first);
                    break;
                }
            }
        }
        const retained = [];
        const columns = {};
        const nodes = [];
        for (let index = 0; index < component.pages_new.length; index++) {
            const element = pageElement(component, index);
            if (!element) continue;
            if (!element.childNodes.length && component.pages_new.length > 1) continue;
            snapshot(component, index);
            columns[retained.length] = component.state.numero_colunas_page?.[index];
            retained.push(component.pages_new[index]);
            nodes.push(Array.from(element.childNodes));
        }
        component.pages_new = retained.length ? retained : [emptyPage()];
        await updateState(component, { pages_new: [...component.pages_new], numero_colunas_page: columns });
        nodes.forEach((children, index) => {
            const element = pageElement(component, index);
            if (element && (children.length !== element.childNodes.length || children.some((child, i) => child !== element.childNodes[i]))) element.replaceChildren(...children);
        });
    } finally {
        restoreBookmark(marker);
        for (let index = 0; index < component.pages_new.length; index++) snapshot(component, index);
    }
}

// Coalesce rapid inputs into a single pagination pass, keeping the latest DOM rather than a queue of HTML copies.
function schedule(component, index, joinPrevious = false) {
    if (component._previewPagination) {
        const pending = component._previewPaginationNext;
        component._previewPaginationNext = { index: Math.min(index, pending?.index ?? index), joinPrevious: joinPrevious || !!pending?.joinPrevious, joinIndex: joinPrevious ? index : pending?.joinIndex };
        return component._previewPagination;
    }
    component._previewPagination = new Promise(resolve => requestAnimationFrame(resolve)).then(async () => {
        let task = { index, joinPrevious, joinIndex: index };
        while (task) {
            await paginate(component, task.index, task.joinPrevious, task.joinIndex);
            task = component._previewPaginationNext;
            component._previewPaginationNext = null;
        }
    }).catch(error => {
        console.error('Falha ao paginar a edição da diagramação.', error);
        component.setState({ msgErro: 'Não foi possível reorganizar as páginas. Seu texto foi mantido; revise a página antes de salvar.' });
    }).finally(() => { component._previewPagination = null; });
    return component._previewPagination;
}

export function handlePreviewInput(component, index, event) {
    component._previewOwner = event?.currentTarget?.closest('#pdf_diagramado_2') || component._previewOwner;
    snapshot(component, index);
    if (!event?.nativeEvent?.isComposing) return schedule(component, index);
    return undefined;
}

export function changePreviewColumns(component, index, count) {
    const root = component._previewOwner || document.querySelector('#pdf_diagramado_2');
    component._previewOwner = root;
    return new Promise(resolve => component.setState(state => index == null
        ? { numero_colunas: count, numero_colunas_page: {} }
        : { numero_colunas_page: { ...state.numero_colunas_page, [index]: count } }, () => {
        for (let page = 0; page < component.pages_new.length; page++) {
            const element = pageElement(component, page);
            if (!element) continue;
            element.style.columnCount = component.state.numero_colunas_page[page] ?? component.state.numero_colunas;
            preparePage(element);
        }
        resolve(schedule(component, index ?? 0));
    }));
}

export function handlePreviewKeyDown(component, index, event) {
    component._previewOwner = event.currentTarget?.closest('#pdf_diagramado_2') || component._previewOwner;
    if (event.key !== 'Backspace' || index === 0 || event.isComposing || event.nativeEvent?.isComposing) return;
    const selection = window.getSelection();
    const element = pageElement(component, index);
    if (!selection?.isCollapsed || !selection.rangeCount || !element) return;
    const prefix = selection.getRangeAt(0).cloneRange();
    prefix.selectNodeContents(element);
    prefix.setEnd(selection.anchorNode, selection.anchorOffset);
    if (significant(prefix.cloneContents())) return;
    event.preventDefault();
    event.stopPropagation();
    return schedule(component, index, true);
}
